import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  ExportFormat,
  exportMerchantTransactions,
} from "../utils/api";
import { getStatusMeta } from "../utils/utils";

/** Values the backend accepts; labels come from the shared status metadata. */
const STATUSES = [
  "created",
  "form_opened",
  "processing",
  "confirmation",
  "completed",
  "canceled",
  "dispute",
];

const TYPES = ["deposit", "payout", "withdrawal", "cashin", "transfer"];

const typeLabel = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

type ExportDialogProps = {
  open: boolean;
  onClose: () => void;
  onNotify: (message: string, severity: "success" | "error" | "info") => void;
};

/**
 * The backend gives the update range priority over the creation range, so the
 * two are offered as one choice rather than four independent fields — picking
 * both would silently drop the creation filter.
 */
type DateBasis = "created" | "updated";

const ExportDialog = ({ open, onClose, onNotify }: ExportDialogProps) => {
  const [basis, setBasis] = useState<DateBasis>("created");
  const [from, setFrom] = useState<Dayjs | null>(null);
  const [to, setTo] = useState<Dayjs | null>(null);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setBasis("created");
    setFrom(null);
    setTo(null);
    setStatuses([]);
    setTypes([]);
    setFormat("csv");
  };

  const submit = async () => {
    setBusy(true);
    onNotify("Preparing your file…", "info");

    const range = {
      from: from ? from.format("DD.MM.YYYY") : null,
      to: to ? to.format("DD.MM.YYYY") : null,
    };

    try {
      const response = await exportMerchantTransactions({
        createdFrom: basis === "created" ? range.from : null,
        createdTo: basis === "created" ? range.to : null,
        updatedFrom: basis === "updated" ? range.from : null,
        updatedTo: basis === "updated" ? range.to : null,
        status: statuses,
        type: types,
        format,
      });

      const blob: Blob = response.data;
      if (!blob || blob.size === 0) {
        onNotify("No transactions match these filters", "error");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `transactions.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onNotify("Export ready", "success");
      reset();
      onClose();
    } catch (error) {
      console.error(error);
      onNotify("Export failed. Please try again.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={() => !busy && onClose()}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Export transactions</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Filter dates by
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={basis}
                onChange={(_event, next: DateBasis | null) =>
                  next && setBasis(next)
                }
              >
                <ToggleButton value="created">Created</ToggleButton>
                <ToggleButton value="updated">Updated</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <DatePicker
                label="From"
                value={from}
                onChange={setFrom}
                format="DD.MM.YYYY"
                maxDate={dayjs()}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label="To"
                value={to}
                onChange={setTo}
                format="DD.MM.YYYY"
                minDate={from ?? undefined}
                maxDate={dayjs()}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>

            <Select
              multiple
              displayEmpty
              size="small"
              value={statuses}
              onChange={(event) =>
                setStatuses(event.target.value as string[])
              }
              renderValue={(selected) =>
                selected.length ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        size="small"
                        label={getStatusMeta(value).label}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    All statuses
                  </Typography>
                )
              }
            >
              {STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {getStatusMeta(status).label}
                </MenuItem>
              ))}
            </Select>

            <Select
              multiple
              displayEmpty
              size="small"
              value={types}
              onChange={(event) => setTypes(event.target.value as string[])}
              renderValue={(selected) =>
                selected.length ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} size="small" label={typeLabel(value)} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    All types
                  </Typography>
                )
              }
            >
              {TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {typeLabel(type)}
                </MenuItem>
              ))}
            </Select>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                File format
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={format}
                onChange={(_event, next: ExportFormat | null) =>
                  next && setFormat(next)
                }
              >
                <ToggleButton value="csv">CSV</ToggleButton>
                <ToggleButton value="xlsx">XLSX</ToggleButton>
              </ToggleButtonGroup>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                {format === "xlsx"
                  ? "Excel workbook with one sheet per currency."
                  : "A single flat list that opens anywhere."}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={busy}>
            {busy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Download"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ExportDialog;
